import Box from '@mui/material/Box';
import { useNotifications } from 'lead-list/components/notifications';
import { useStateMachine } from 'little-state-machine';
import { FC, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { StepImporting } from './components/StepImporting';
import { StepMapColumns } from './components/StepMapColumns';
import { StepReview } from './components/StepReview';
import { StepSelectFile } from './components/StepSelectFile';
import { StepSelectLeadType } from './components/StepSelectLeadType';
import { clearLeadListCreate, setLeadListCreate } from './state/actions';

export const STEPS = {
  fileUpload: 'file-upload',
  selectCampaign: 'select-campaign',
  mapColumns: 'map-columns',
  review: 'review',
  importing: 'importing',
};

const LeadListCreateView: FC = () => {
  const location = useLocation();
  const transitionClass = 'slide-down';

  const { actions, state } = useStateMachine({ setLeadListCreate, clearLeadListCreate });
  const { notifyError } = useNotifications();

  useEffect(() => {
    const state = location.state as { file?: File } | null;
    if (state?.file !== undefined) {
      actions.setLeadListCreate({ file: state.file });
    }
    return () => {
      actions.clearLeadListCreate(undefined);
    };
  }, []);

  useEffect(() => {
    if (state.leadListCreate?.file?.name) {
      parseFile(state.leadListCreate.file);
    }
  }, [state.leadListCreate.file]);

  const parseFile = (file: File) => {
    if (!['text/csv', 'application/vnd.ms-excel'].includes(file.type)) {
      notifyFileError();
      return;
    }

    if (file.size < 10 || file.name.length > 256) {
      notifyFileError();
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const text = reader.result;
      if (!text || text instanceof ArrayBuffer) return;

      const csvHeader = text.split('\n', 1)[0];
      const fileHeaders = csvHeader
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean);

      const estimatedTotalRows = (text.match(/\r?\n/g) || []).length;

      actions.setLeadListCreate({
        fileProcessingError: false,
        fileProcessed: true,
        fileHeaders,
        mappings: {},
        estimatedTotalRows,
      });
    };
    reader.readAsText(file);
  };

  const notifyFileError = () => {
    notifyError(
      'Invalid file. Please upload a valid CSV file with sufficient data and a reasonable name.',
      'fileTypeError',
    );
    actions.setLeadListCreate({
      fileProcessed: false,
      fileProcessingError: true,
      file: null,
    });
  };

  return (
    <Box
      maxWidth="sm"
      mx="auto"
      px={2}
      py={4}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '460px',
      }}
    >
      <TransitionGroup component={null}>
        <CSSTransition
          key={location.pathname}
          classNames={transitionClass}
          timeout={400}
          unmountOnExit
        >
          <div style={{ position: 'relative', width: '100%' }}>
            <Routes location={location}>
              <Route path="" element={<StepSelectFile />} />
              <Route path={STEPS.selectCampaign} element={<StepSelectLeadType />} />
              <Route path={STEPS.mapColumns} element={<StepMapColumns />} />
              <Route path={STEPS.review} element={<StepReview />} />
              <Route path={STEPS.importing} element={<StepImporting />} />
              <Route path="*" element={<Navigate to="" replace />} />
            </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </Box>
  );
};

export default LeadListCreateView;
